import { throttleRaf } from '../utils/throttleRaf';
import { getCurrentInstance } from 'vue';

/**
 * useOptimizedScroll
 *
 * Composable that provides the optimized scroll utility for debate views.
 * Returns a throttled function you can call with (roundNumber, selectAnnId).
 *
 * This is not a pure composable - context is a "this" reference to the component instance.
 *
 * We need to fully migrate this to a real composable in the future.
 *
 */
export function useOptimizedScroll(context?: any) {
  if (!context) {
    const vm = getCurrentInstance();
    context = (vm as any)?.proxy ?? context;
  }
  const throttledNewScroll = throttleRaf((roundNumber: number, selectAnnId: string) => {
    _newScrollImpl(roundNumber, selectAnnId);
  });

  const _newScrollImpl = async (roundNumber: number, selectAnnId: string) => {
    if (!context.counterpointData || context.showCounterpointSelect) {
      return;
    }

    // Pre-build DOM caches to avoid expensive lookups in nested loops
    const idCache = new Map<string, HTMLElement | null>();
    const refCache = new Map<string, Element | null>();

    context.counterpoints.forEach((cp: any) => {
      cp.annotations.forEach((a: any) => {
        idCache.set(a._id, document.getElementById(`annotation-${a._id}`));
        const refElement = document.getElementsByClassName(`counterpoint-ref-${a._id}`)[0];
        refCache.set(a._id, (refElement as Element) || null);
        if (a.refAnnotation?._id) {
          idCache.set(
            a.refAnnotation._id,
            document.getElementById(`annotation-${a.refAnnotation._id}`)
          );
        }
      });
    });

    const containerElement = document.getElementById(
      context.mainPanelView === 'article' ? 'annotatorArticle' : 'annotatorOutline'
    );

    // Global height cache to track all annotation heights for panel stretching
    const globalHeightCache = new Map<string, number>();

    for (const cpdata of context.counterpoints as any[]) {
      // We introduce a Promise here to wait for each round to complete its layout.
      // We probably want to drop it and instead use top of the previous annotation
      // by ID instead of waiting for the layout to complete and using boundingClientRect.
      await new Promise<void>(resolve => {
        const container = containerElement?.getBoundingClientRect();
        const cp = context.$refs[`cp${cpdata._id}`] ? context.$refs[`cp${cpdata._id}`][0] : null;
        const activeRound =
          roundNumber === cpdata.round ||
          context.counterpointData.rounds.length === cpdata.round ||
          roundNumber === 0;

        if (cp && activeRound && !context.useExcerpts) {
          let annotations = cpdata.annotations as any[];

          if (context.mainPanelView === 'outline') {
            const outlineAnnKeys = Object.keys(context.outlineAnnotations);
            annotations.sort((a: any, b: any) => {
              const aKey = outlineAnnKeys.indexOf(
                outlineAnnKeys.find(key =>
                  context.outlineAnnotations[key].some(
                    (ann: any) =>
                      ann._id ===
                      (context.getAnnotation(a.refAnnotation?._id)?.refAnnotation?._id ||
                        a.refAnnotation?._id ||
                        a._id)
                  )
                ) as string
              );
              const bKey = outlineAnnKeys.indexOf(
                outlineAnnKeys.find(key =>
                  context.outlineAnnotations[key].some(
                    (ann: any) =>
                      ann._id ===
                      (context.getAnnotation(b.refAnnotation?._id)?.refAnnotation?._id ||
                        b.refAnnotation?._id ||
                        b._id)
                  )
                ) as string
              );
              return aKey - bKey;
            });
            if (!annotations.length) annotations = cpdata.annotations;
          }

          const activeAnnotations = annotations.filter(
            (annotation: any) =>
              selectAnnId === annotation._id ||
              context.counterpointData.rounds.length === cpdata.round ||
              selectAnnId === 'temp'
          );

          if (!activeAnnotations.length) {
            resolve();
            return;
          }

          // Collect all layout data FIRST (read phase) - including height calculations
          const computedTop = new Map<string, number>();
          const computedHeight = new Map<string, number>();
          const sortedAnnotations: Array<{
            annotation: any;
            elm: HTMLElement;
            originalIndex: number;
          }> = [];

          activeAnnotations.forEach((annotation: any) => {
            const elm = idCache.get(annotation._id) as HTMLElement | null;
            if (!container || !elm) {
              return;
            }

            // Read all geometry data without any style mutations
            let parentElm: Partial<DOMRect> = {};
            let parentElmRef: Element | null = null;
            const annRefFirst = refCache.get(annotation._id) as Element | null;
            const annRefSecond = idCache.get(annotation.refAnnotation?._id) as HTMLElement | null;

            if (annRefFirst || annRefSecond) {
              parentElmRef = !annotation.refAnnotation?._id ? annRefFirst : annRefSecond;
              if (parentElmRef) {
                parentElm = (parentElmRef as HTMLElement).getBoundingClientRect();
              }
            }

            const elmRect = elm.getBoundingClientRect();

            let margin = 0;
            if (annotation.refAnnotation?._id) {
              const parentTextRef = refCache.get(annotation._id) as HTMLElement | null;
              if (parentTextRef) margin = parseInt(String((parentTextRef as any).offsetTop)) || 0;
            }

            // Compute the ideal top position based on content position
            const idealTop =
              (parentElm.top || 0) -
              (container.top || 0) -
              (cpdata.round > 1 ? 15 : 32) -
              elmRect.height / 2 +
              margin;

            computedTop.set(annotation._id, Math.max(0, idealTop));
            computedHeight.set(annotation._id, elmRect.height);

            // Store in global cache for panel stretching
            globalHeightCache.set(annotation._id, elmRect.height);

            sortedAnnotations.push({
              annotation,
              elm,
              originalIndex: annotations.indexOf(annotation),
            });
          });

          // Sort annotations by their ideal top position
          // sortedAnnotations.sort((a, b) => {
          //   const topA = computedTop.get(a.annotation._id) || 0;
          //   const topB = computedTop.get(b.annotation._id) || 0;
          //   return topA - topB;
          // });

          // Now apply all style mutations (write phase) with running top calculation
          // Use requestAnimationFrame to wait for the next paint cycle before applying styles.
          requestAnimationFrame(() => {
            let runningTop = 0;
            const idsToMakeVisible: string[] = [];

            sortedAnnotations.forEach(({ annotation, elm }) => {
              (elm as HTMLElement).style.position = 'absolute';

              // Use the greater of: running top or computed ideal position
              const topVal = Math.max(runningTop, computedTop.get(annotation._id) || 0);
              if (isNaN(topVal)) {
                return;
              }

              context.setAnnotationTop({ _id: annotation._id, value: topVal });
              const height = computedHeight.get(annotation._id) || 0;
              runningTop = topVal + height + 20;

              // Collect IDs to make them visible all at once in the next frame.
              idsToMakeVisible.push(annotation._id);
              context.incrementScrollCounter();
            });

            // Use another requestAnimationFrame to make all annotations visible on the *next* frame
            requestAnimationFrame(() => {
              idsToMakeVisible.forEach(_id => {
                context.setAnnotationvisible({ _id, value: true });
              });
              // Resolve the promise after the styles for this round have been applied and painted.
              resolve();
            });
          });
        } else {
          resolve(); // Resolve if the round is not active or has no component
        }
      });
    }

    // After all rounds are processed, stretch all annotations panels to cover the bottom-most annotation
    const stretchAnnotationsPanels = () => {
      // Calculate height from rootTextCells
      const rootTextCells = document.getElementById('rootTextCells');
      const rootTextCellsHeight = rootTextCells
        ? rootTextCells.getBoundingClientRect().height + 100
        : 0;

      // Calculate from annotation positions
      let maxBottomPosition = 0;

      // Find the bottom-most annotation across all panels
      context.counterpoints.forEach((cp: any) => {
        cp.annotations.forEach((a: any) => {
          const elm = idCache.get(a._id) as HTMLElement | null;
          if (elm && (elm as HTMLElement).style.position === 'absolute') {
            const topPosition = parseFloat((elm as HTMLElement).style.top || '0') || 0;
            const height = globalHeightCache.get(a._id) || 0;
            const bottomPosition = topPosition + height;
            maxBottomPosition = Math.max(maxBottomPosition, bottomPosition);
          }
        });
      });

      const annotationsHeight = maxBottomPosition + 250; // Add some padding at the bottom

      // Use the larger of the two heights
      const targetHeight = Math.max(rootTextCellsHeight, annotationsHeight);

      // Apply the height to all annotations panels if we found a valid height
      if (targetHeight > 0) {
        const annotationsPanels = document.querySelectorAll('.annotations-panel');
        annotationsPanels.forEach(panel => {
          (panel as HTMLElement).style.minHeight = `${targetHeight}px`;
        });
      }
    };

    // Call stretchAnnotationsPanels after a brief delay to ensure all positioning is complete
    requestAnimationFrame(() => {
      setTimeout(stretchAnnotationsPanels, 50);
    });
  };

  return throttledNewScroll;
}
